<template>
  <div
    v-if="isSupported"
    ref="mapContainer"
    class="map-container"
    :class="{ 'map-container--fullscreen': mapFullScreen }"
  >
    <div class="map-container__buttons">
      <button
        class="button button--secondary button--filter"
        @click="changeVersion(true)"
        :disabled="store.mapVersion === true"
      >
        <span class="icon icon--earth"></span>
        <span>Kartvy ({{ store.numResultWithCoordinates }})</span>
      </button>
      <button class="button button--filter" @click="changeVersion(false)">
        <span class="icon icon--list"></span>
        <span>Listvy ({{ store.numTotalResult }})</span>
      </button>
    </div>
    <div class="filtering"></div>
  </div>
  <div v-else class="map-unsupported">
    <p>
      Din webbläsare stödjer tyvärr inte kartfunktionen. Vänligen uppdatera din
      webbläsare till en nyare version.
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from "vue";
import { useStore } from "../../stores/store";
import { useRouter } from "vue-router";
import mapboxgl from "mapbox-gl";
import type { Point } from "geojson";

const store = useStore();
const router = useRouter();
const imageRootPath: string = process.env.ImageRootPath;
const mapboxAccessToken: string | undefined = process.env.MapboxAccessToken;

const props = defineProps<{
  markers: {
    features: Array<any>;
  };
}>();

const mapContainer = ref<HTMLDivElement | null>(null);
const mapFullScreen = ref(false);
const isSupported = ref(true);
const renderedMarkers = ref<GeoJSON.FeatureCollection | null>(null);
let map: mapboxgl.Map | null = null;
let hoveredId: string | null = null;
const markersSourceId = "markers-source";
const clustersLayerId = "clusters";
const clusterCountLayerId = "cluster-count";
const unclusteredLayerId = "unclustered-markers";

onMounted(() => {
  if (!mapboxgl.supported()) {
    isSupported.value = false;
    return;
  }

  if (!mapboxAccessToken) {
    console.error(
      "Missing Mapbox access token. Set process.env.MapboxAccessToken.",
    );
    return;
  }

  mapboxgl.accessToken = mapboxAccessToken;

  if (props.markers) {
    initializeMap();
  }
});

watch(
  () => props.markers,
  (newVal, oldVal) => {
    if (oldVal === undefined && newVal !== undefined) {
      // Initialize the map only when it changes from undefined to a value
      initializeMap();
      store.loading = false;
    }
    store.numResultWithCoordinates = newVal.features.length;
    applyRenderedMarkers(hoveredId);
  },
  { deep: true },
);

watch(
  () => store.mapVersion,
  (newVal, oldVal) => {
    if (newVal) {
      nextTick(() => map?.resize());
    }
  },
);

const changeVersion = (value: boolean) => {
  store.mapVersion = value;
};

const normalizeMarkerId = (id: unknown): string | null => {
  if (typeof id === "string" || typeof id === "number") {
    return String(id);
  }
  return null;
};

const buildRenderedMarkers = (currentHoveredId: string | null) => {
  const features = (props.markers?.features ?? []).map((marker: any) => {
    const markerId = normalizeMarkerId(marker?.properties?.id);
    return {
      ...marker,
      properties: {
        ...marker?.properties,
        icon:
          markerId === currentHoveredId
            ? "custom-marker-hover"
            : "custom-marker",
      },
    };
  });

  return {
    type: "FeatureCollection",
    features,
  } as GeoJSON.FeatureCollection;
};

const applyRenderedMarkers = (currentHoveredId: string | null) => {
  renderedMarkers.value = buildRenderedMarkers(currentHoveredId);
  updateMarkers(renderedMarkers.value);
};

const initializeMap = () => {
  if (!mapboxAccessToken) {
    isSupported.value = false;
    console.error(
      "Missing Mapbox access token. Set process.env.MapboxAccessToken.",
    );
    return;
  }

  mapboxgl.accessToken = mapboxAccessToken;

  if (!mapContainer.value) {
    return;
  }

  if (map) {
    map.remove(); // Removes the old instance
  }

  hoveredId = null;

  map = new mapboxgl.Map({
    container: mapContainer.value,
    style: "mapbox://styles/mapbox/streets-v12",
    center: [15.0, 62.0], // [longitude, latitude] – roughly center of Sweden
    zoom: 4,
  });

  map.addControl(
    new mapboxgl.NavigationControl({ showCompass: false }),
    "bottom-right",
  );
  let markerImage = `${imageRootPath}/vrak-black-marker.png`;
  let markerImageHover = `${imageRootPath}/vrak-orange-marker.png`;

  map.on("load", () => {
    map?.loadImage(markerImage, (error: any, image: any) => {
      if (error || !image) {
        console.error("Failed to load marker image", {
          src: markerImage,
          error,
        });
        return;
      }

      map?.loadImage(markerImageHover, (error2: any, hoverImage: any) => {
        if (error2 || !hoverImage) {
          console.error("Failed to load hover marker image", {
            src: markerImageHover,
            error: error2,
          });
          return;
        }

        map?.addImage("custom-marker", image);
        map?.addImage("custom-marker-hover", hoverImage);

        map?.addSource(markersSourceId, {
          type: "geojson",
          data: renderedMarkers.value ?? buildRenderedMarkers(hoveredId),
          cluster: true,
          clusterRadius: 30,
          clusterMaxZoom: 12,
        });

        map?.addLayer({
          id: clustersLayerId,
          type: "circle",
          source: markersSourceId,
          filter: ["has", "point_count"],
          paint: {
            "circle-color": "#000000",
            "circle-radius": [
              "step",
              ["get", "point_count"],
              18,
              25,
              24,
              75,
              30,
            ],
            "circle-stroke-width": 2,
            "circle-stroke-color": "#f36b41",
          },
        });

        map?.addLayer({
          id: clusterCountLayerId,
          type: "symbol",
          source: markersSourceId,
          filter: ["has", "point_count"],
          layout: {
            "text-field": ["get", "point_count_abbreviated"],
            "text-font": ["DIN Offc Pro Bold", "Arial Unicode MS Bold"],
            "text-size": [
              "step",
              ["get", "point_count"],
              14, // < 25
              25,
              18, // 25–74
              75,
              20, // 75+
            ],
          },
          paint: {
            "text-color": "#f36b41",
          },
        });

        map?.addLayer({
          id: unclusteredLayerId,
          type: "symbol",
          source: markersSourceId,
          filter: ["!", ["has", "point_count"]],
          layout: {
            "icon-image": ["get", "icon"],
            "icon-size": ["interpolate", ["linear"], ["zoom"], 0, 0.15],
            "icon-allow-overlap": true,
          },
        });

        // Change the marker icon on hover
        map?.on("mouseenter", unclusteredLayerId, (e) => {
          if (!map || !e.features?.length) {
            return;
          }
          map.getCanvas().style.cursor = "pointer";
          const feature = e.features[0];
          const id = normalizeMarkerId(feature?.properties?.id);

          if (!id || hoveredId === id) {
            return; // Already hovered
          }

          hoveredId = id;
          applyRenderedMarkers(hoveredId);
        });

        map?.on("mouseleave", unclusteredLayerId, () => {
          if (!map) {
            return;
          }

          map.getCanvas().style.cursor = "";
          if (hoveredId !== null) {
            hoveredId = null;
            applyRenderedMarkers(hoveredId);
          }
        });
      });
    });
  });

  let popup = new mapboxgl.Popup({
    closeButton: true,
    closeOnClick: true,
  });

  map.on("click", clustersLayerId, (e) => {
    if (!map || !e.features?.length) {
      return;
    }

    const feature = e.features[0];
    const geometry = feature.geometry;
    if (geometry.type !== "Point") {
      return;
    }

    const clusterId = Number(feature.properties?.cluster_id);
    const source = map.getSource(markersSourceId) as
      | mapboxgl.GeoJSONSource
      | undefined;

    if (!source || !Number.isFinite(clusterId)) {
      return;
    }

    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err || zoom === undefined || !map) {
        return;
      }

      map.easeTo({
        center: (geometry as Point).coordinates as [number, number],
        zoom,
      });
    });
  });

  map.on("click", unclusteredLayerId, (e) => {
    if (!map || !e.features?.length) {
      return;
    }

    const feature = e.features[0];
    const geometry = feature.geometry;

    if (geometry.type !== "Point") {
      return;
    }

    const coordinates = (geometry as Point).coordinates.slice();
    const id = feature.properties?.id;
    const matchingShip = store.originalSearchResult?.results.find(
      (ship: any) => ship.uuid === id,
    );

    if (matchingShip) {
      popup
        .setLngLat(coordinates as [number, number]) // We know they're like [lng, lat]
        .setHTML(
          `<div class="popup" data-ship-id="${matchingShip.uuid}">
                <h4>${matchingShip.name}</h4>
                ${
                  matchingShip.alternateNames?.length
                    ? `<p>(${matchingShip.alternateNames.join(", ")})</p>`
                    : ""
                }
                <p>${matchingShip.type ?? ""}</p>
                <span class="icon icon--arrow-dash-right"></span>
                <span class="popup__pattern"></span>
            </div>`,
        )
        .addTo(map);
    }

    // Add click event listener to the button inside the popup
    const popupContent = document.querySelector(".popup");
    popupContent?.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const wrapper = target.closest("[data-ship-id]");
      const id = wrapper?.getAttribute("data-ship-id");
      router.push({ name: "Details", params: { shipId: id } });
    });
  });
};

const updateMarkers = (markersGeoJSON: GeoJSON.FeatureCollection) => {
  const source = map?.getSource(markersSourceId) as
    | mapboxgl.GeoJSONSource
    | undefined;
  source?.setData(markersGeoJSON);
};
</script>
